import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL } from "@/config/urls";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-ophira-dark/80 backdrop-blur-md py-3" 
          : "bg-ophira-dark/90 py-5 pb-5"
      }`}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-12 w-12 flex items-center justify-center">
            <img src="/logo.png" alt="YouAux Logo" className="w-full h-full object-contain" />
          </div>
          <span className="font-bold text-xl text-white">YouAux</span>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-2">
          <Link to="/sell-your-music" className={`nav-link ${isActive('/sell-your-music') ? 'active' : ''}`}>Sell Your Music</Link>
          <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
          <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
          <Link to="/blog" className={`nav-link ${isActive('/blog') ? 'active' : ''}`}>Blog</Link>
          <Link to="/pricing" className={`nav-link ${isActive('/pricing') ? 'active' : ''}`}>Pricing</Link>
          <Link to="/services" className={`nav-link ${isActive('/services') ? 'active' : ''}`}>Services</Link>
          {/* <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact</Link> */}
          {/* <Link to="/my-account" className={`nav-link ${isActive('/my-account') ? 'active' : ''}`}>My Account</Link> */}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a href={DASHBOARD_SIGNIN_URL} className="text-white hover:text-ophira-yellow transition-colors">
            Sign In
          </a>
          <a href={DASHBOARD_SIGNUP_URL} className="ml-4">
            <Button className="ophira-button">Sign Up</Button>
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-white focus:outline-none"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          id="mobile-menu"
          className="md:hidden absolute top-full left-0 right-0 bg-ophira-dark/95 backdrop-blur-md border-t border-ophira-darkgray/50 py-4 px-4 animate-fade-in"
        >
          <nav className="flex flex-col gap-4">
            <Link to="/sell-your-music" className={`nav-link ${isActive('/sell-your-music') ? 'active' : ''}`}>Sell Your Music</Link>
            <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
            <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
            <Link to="/blog" className={`nav-link ${isActive('/blog') ? 'active' : ''}`}>Blog</Link>
            <Link to="/pricing" className={`nav-link ${isActive('/pricing') ? 'active' : ''}`}>Pricing</Link>
            <Link to="/services" className={`nav-link ${isActive('/services') ? 'active' : ''}`}>Services</Link>
            {/* <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact</Link> */}
          </nav>
          <div className="flex flex-col gap-3 mt-6">
            <a href={DASHBOARD_SIGNIN_URL}>
              <Button variant="outline" className="w-full border-ophira-yellow/50 text-white rounded-[0.55rem]">Sign In</Button>
            </a>
            <a href={DASHBOARD_SIGNUP_URL}>
              <Button className="w-full ophira-button">Sign Up</Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
